// @ts-nocheck
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Alert,
  Button,
  InputAdornment,
  LinearProgress,
  styled,
  TextField,
  Typography,
  Toolbar,
  Stack,
} from "@mui/material";
import {
  DataGrid,
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
  // Toolbar from grid
} from "@mui/x-data-grid";
import { string, array, bool, object, element, func, number } from "prop-types";
import { Box } from "@mui/system";
import "./Styles/TableView1.css";
import { useTextDomainContext } from "@/app/providers";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  EnergyReportDropdown,
  ExportCustomDropdown,
  ExportDropdown,
  ExportEnergyStatementDropdown,
  ExportTariffStatementDropdown,
} from "..";
import { addIdToArrayOfObjects, gridDataToExportArray } from "@/utils";
import SearchIcon from "@mui/icons-material/Search";
import CancelIcon from "@mui/icons-material/Cancel";

export const TableView1 = (props) => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  const showGridToolbar = Boolean(props.showGridToolbar);
  const showGridToolbarCustomExport = Boolean(
    props.showGridToolbarCustomExport,
  );
  const showGridToolbarExport = Boolean(props.showGridToolbarExport);
  const showGridToolbarEnergyStatementExport = Boolean(
    props.showGridToolbarEnergyStatementExport,
  );
  const showGridToolbarTariffStatementExport = Boolean(
    props.showGridToolbarTariffStatementExport,
  );
  const showGridToolbarQuickFilter = Boolean(props.showGridToolbarQuickFilter);
  const showGridToolbarDeleteButton = Boolean(
    props.showGridToolbarDeleteButton,
  );
  const showEnergyStatementType = Boolean(props.showEnergyStatementType);

  //#region useMemo
  const excelDataForExport = useMemo(() => {
    return props.excelData ?? [];
  }, [props.excelData]);

  const customDataForExport = useMemo(() => {
    return props.exportData ?? [];
  }, [props.exportData]);

  const rows = useMemo(() => {
    return props.rows ? addIdToArrayOfObjects(props.rows) : [];
  }, [props.rows]);

  const columns = useMemo(() => {
    return props.columns ?? [];
  }, [props.columns]);

  const rowCount = useMemo(() => {
    return props.rowCount || -1;
  }, [props.rowCount]);

  const infoText = useMemo(() => {
    return props.infoText || "";
  }, [props.infoText]);

  const headerRight = useMemo(() => {
    return props.headerRight || <></>;
  }, [props.headerRight]);

  const beforeTable = useMemo(() => {
    return props.beforeTable || <></>;
  }, [props.beforeTable]);

  const afterTable = useMemo(() => {
    return props.afterTable || <></>;
  }, [props.afterTable]);

  const title = useMemo(() => {
    return props.title || "";
  }, [props.title]);

  const fileNameForExport = useMemo(() => {
    return props.fileNameForExport || "export";
  }, [props.fileNameForExport]);

  const preToolbar = useMemo(() => {
    return props.preToolbar || "";
  }, [props.preToolbar]);

  const postToolbar = useMemo(() => {
    return props.postToolbar || "";
  }, [props.postToolbar]);

  const dataForExport = useMemo(() => {
    let returnValue = [];
    if (rows?.length && columns?.length) {
      returnValue = gridDataToExportArray(columns, rows);
    }
    return returnValue;
  }, [columns, rows]);

  const propsDataGrid = useMemo(() => {
    const dataMode = props.dataMode || "client";
    const columnGroupingModel = props.columnGroupingModel ?? [];
    let newPropsDataGrid = {};

    if (dataMode === "server") {
      newPropsDataGrid = {
        ...newPropsDataGrid,
        rowCount: rowCount,
        sortingMode: dataMode,
        filterMode: dataMode,
        paginationMode: dataMode,
      };
    }

    if (props.disableColumnSorting) {
      newPropsDataGrid = {
        ...newPropsDataGrid,
        disableColumnSorting: props.disableColumnSorting,
      };
    }

    if (props.disableColumnSelector) {
      newPropsDataGrid = {
        ...newPropsDataGrid,
        disableColumnSelector: props.disableColumnSelector,
      };
    }

    if (columnGroupingModel.length) {
      newPropsDataGrid = {
        ...newPropsDataGrid,
        columnGroupingModel: props.columnGroupingModel,
      };
    }

    if (props.checkboxSelection) {
      newPropsDataGrid = {
        ...newPropsDataGrid,
        checkboxSelection: props.checkboxSelection,
      };
    }

    if (props.getRowHeight) {
      newPropsDataGrid = {
        ...newPropsDataGrid,
        getRowHeight: props.getRowHeight,
      };
    }

    if (props.hideFooterPagination) {
      newPropsDataGrid = {
        ...newPropsDataGrid,
        hideFooterPagination: props.hideFooterPagination,
      };
    }

    if (props.hideFooter) {
      newPropsDataGrid = {
        ...newPropsDataGrid,
        hideFooter: props.hideFooter,
      };
    }

    return newPropsDataGrid;
  }, [
    props.checkboxSelection,
    props.columnGroupingModel,
    props.dataMode,
    props.disableColumnSelector,
    props.disableColumnSorting,
    props.getRowHeight,
    props.hideFooterPagination,
    props.hideFooter,
    rowCount,
  ]);

  //#endregion useMemo

  const propsDefault = {
    loading: false,
    checkboxSelection: false,
    pageSizeOptions: [5, 10, 50, 100],
    initialState: {
      pagination: {
        paginationModel: {
          page: 0,
          pageSize: 100,
        },
      },
    },
  };

  //region useState [React Hooks] (useState)
  const [disabledDeleteButton, setDisabledDeleteButton] = useState(true);
  const [newSelectedIds, setNewSelectedIds] = useState<any[] | null>(null);
  //endregion useState

  //#region useMemo
  const isControlled = props.selectedIds !== undefined;

  const selectedIds = useMemo(() => {
    if (isControlled) {
      return props.selectedIds || [];
    }
    return newSelectedIds || [];
  }, [isControlled, props.selectedIds, newSelectedIds]);

  const rowSelectionModel = useMemo(() => {
    const idsArray = Array.isArray(selectedIds)
      ? selectedIds
      : Array.from((selectedIds as any) || []);
    return {
      type: "include" as const,
      ids: new Set(idsArray),
    };
  }, [selectedIds]);
  //#endregion useMemo

  useEffect(() => {
    const count = Array.isArray(selectedIds)
      ? selectedIds.length
      : (selectedIds as any)?.size || 0;
    setDisabledDeleteButton(count === 0);
  }, [selectedIds]);

  const handleOnRowSelectionModelChange = (event: any) => {
    let ids: Array<string | number> = [];
    if (Array.isArray(event)) {
      ids = event;
    } else if (event && event.ids) {
      ids = Array.from(event.ids);
    } else if (event) {
      ids = Array.from(event);
    }

    if (props.checkboxSelection) {
      if (!isControlled) {
        setNewSelectedIds(ids);
      }
      if (typeof props.onRowSelectionModelChange === "function") {
        props.onRowSelectionModelChange(ids);
      }
    }
  };

  const handleOnChangeEnergyReportDropdown = useCallback(
    (obj) => {
      if (typeof props.onChangeEnergyStatement === "function") {
        props.onChangeEnergyStatement(obj);
      }
    },
    [props],
  );

  const CustomToolbar = useCallback(() => {
    return (
      <>
        {showGridToolbar && (
          <Box
            // sx={{
            //   position: "absolute",
            //   top: -85,
            //   width: "100%",
            //   p: 0,
            //   m: 0,
            //   // backgroundColor: "yellow",
            // }}
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              borderBottom: "1px solid #ccc",
              borderTop: "2px solid #FFF",
              // height: 200,
              // border: '1px solid black',
            }}
          >
            <Box>{title && <Typography variant="h4">{title}</Typography>}</Box>

            <Box sx={{ flexGrow: 1 }} />

            <Box>
              <Toolbar
                disableGutters
                sx={{
                  border: "none",
                }}
              >
                {preToolbar}
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    pl: 1,
                    alignItems: "center",
                  }}
                >
                  {showGridToolbarQuickFilter && (
                    <QuickFilter defaultExpanded>
                      <QuickFilterControl
                        {...({
                          render: (
                            { ref, ...controlProps }: any,
                            state: any,
                          ) => (
                            <TextField
                              {...controlProps}
                              {...({
                                ownerState: { expanded: state.expanded },
                              } as any)}
                              inputRef={ref}
                              aria-label="Search"
                              placeholder="Search..."
                              size="small"
                              slotProps={{
                                input: {
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                  ),
                                  endAdornment: state.value ? (
                                    <InputAdornment position="end">
                                      <QuickFilterClear
                                        edge="end"
                                        size="small"
                                        aria-label="Clear search"
                                        material={{
                                          sx: { marginRight: -0.75 },
                                        }}
                                      >
                                        <CancelIcon fontSize="small" />
                                      </QuickFilterClear>
                                    </InputAdornment>
                                  ) : null,
                                  ...controlProps.slotProps?.input,
                                },
                                ...controlProps.slotProps,
                              }}
                            />
                          ),
                          sx: {
                            minWidth: 200,
                          },
                        } as any)}
                      />
                    </QuickFilter>
                  )}

                  {showEnergyStatementType && (
                    <EnergyReportDropdown
                      onChange={handleOnChangeEnergyReportDropdown}
                      selectedOption={props.selectedEnergyStatement}
                    />
                  )}

                  {showGridToolbarExport && (
                    <ExportDropdown
                      data={dataForExport}
                      fileName={fileNameForExport}
                    />
                  )}

                  {showGridToolbarEnergyStatementExport && (
                    <ExportEnergyStatementDropdown
                      data={excelDataForExport}
                      fileName={fileNameForExport}
                    />
                  )}

                  {showGridToolbarTariffStatementExport && (
                    <ExportTariffStatementDropdown
                      data={excelDataForExport}
                      fileName={fileNameForExport}
                    />
                  )}

                  {showGridToolbarCustomExport && (
                    <ExportCustomDropdown data={customDataForExport} />
                  )}

                  {showGridToolbarDeleteButton && (
                    <Button
                      startIcon={<DeleteIcon />}
                      onClick={() => {
                        if (typeof props.onClickDeleteMultiple === "function") {
                          props.onClickDeleteMultiple(selectedIds);
                        }
                      }}
                      disabled={disabledDeleteButton}
                      sx={{
                        color: "error.main",
                      }}
                    >
                      {gettext("Delete")}
                    </Button>
                  )}

                  {postToolbar}
                </Stack>

                {/*{postToolbar && (*/}
                {/*  <Box sx={{marginLeft: 2}}>*/}
                {/*    {postToolbar}*/}
                {/*  </Box>*/}
                {/*)}*/}

                {headerRight}
              </Toolbar>
            </Box>
          </Box>
        )}
      </>
    );
  }, [
    showGridToolbar,
    title,
    preToolbar,
    showGridToolbarQuickFilter,
    showEnergyStatementType,
    handleOnChangeEnergyReportDropdown,
    props,
    showGridToolbarExport,
    dataForExport,
    fileNameForExport,
    showGridToolbarEnergyStatementExport,
    excelDataForExport,
    showGridToolbarTariffStatementExport,
    showGridToolbarCustomExport,
    customDataForExport,
    showGridToolbarDeleteButton,
    disabledDeleteButton,
    gettext,
    postToolbar,
    headerRight,
    selectedIds,
  ]);

  const StyledGridOverlay = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    "& .ant-empty-img-1": {
      fill: theme.palette.mode === "light" ? "#aeb8c2" : "#262626",
    },
    "& .ant-empty-img-2": {
      fill: theme.palette.mode === "light" ? "#f5f5f7" : "#595959",
    },
    "& .ant-empty-img-3": {
      fill: theme.palette.mode === "light" ? "#dce0e6" : "#434343",
    },
    "& .ant-empty-img-4": {
      fill: theme.palette.mode === "light" ? "#fff" : "#1c1c1c",
    },
    "& .ant-empty-img-5": {
      fillOpacity: theme.palette.mode === "light" ? "0.8" : "0.08",
      fill: theme.palette.mode === "light" ? "#f5f5f5" : "#fff",
    },
  }));

  const CustomNoRowsOverlay = () => {
    return (
      <StyledGridOverlay>
        {/* <svg */}
        {/*   style={{ flexShrink: 0 }} */}
        {/*   width="240" */}
        {/*   height="200" */}
        {/*   viewBox="0 0 184 152" */}
        {/*   aria-hidden */}
        {/*   focusable="false" */}
        {/* > */}
        {/*   <g fill="none" fillRule="evenodd"> */}
        {/*     <g transform="translate(24 31.67)"> */}
        {/*       <ellipse */}
        {/*         className="ant-empty-img-5" */}
        {/*         cx="67.797" */}
        {/*         cy="106.89" */}
        {/*         rx="67.797" */}
        {/*         ry="12.668" */}
        {/*       /> */}
        {/*       <path */}
        {/*         className="ant-empty-img-1" */}
        {/*         d="M122.034 69.674L98.109 40.229c-1.148-1.386-2.826-2.225-4.593-2.225h-51.44c-1.766 0-3.444.839-4.592 2.225L13.56 69.674v15.383h108.475V69.674z" */}
        {/*       /> */}
        {/*       <path */}
        {/*         className="ant-empty-img-2" */}
        {/*         d="M33.83 0h67.933a4 4 0 0 1 4 4v93.344a4 4 0 0 1-4 4H33.83a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z" */}
        {/*       /> */}
        {/*       <path */}
        {/*         className="ant-empty-img-3" */}
        {/*         d="M42.678 9.953h50.237a2 2 0 0 1 2 2V36.91a2 2 0 0 1-2 2H42.678a2 2 0 0 1-2-2V11.953a2 2 0 0 1 2-2zM42.94 49.767h49.713a2.262 2.262 0 1 1 0 4.524H42.94a2.262 2.262 0 0 1 0-4.524zM42.94 61.53h49.713a2.262 2.262 0 1 1 0 4.525H42.94a2.262 2.262 0 0 1 0-4.525zM121.813 105.032c-.775 3.071-3.497 5.36-6.735 5.36H20.515c-3.238 0-5.96-2.29-6.734-5.36a7.309 7.309 0 0 1-.222-1.79V69.675h26.318c2.907 0 5.25 2.448 5.25 5.42v.04c0 2.971 2.37 5.37 5.277 5.37h34.785c2.907 0 5.277-2.421 5.277-5.393V75.1c0-2.972 2.343-5.426 5.25-5.426h26.318v33.569c0 .617-.077 1.216-.221 1.789z" */}
        {/*       /> */}
        {/*     </g> */}
        {/*     <path */}
        {/*       className="ant-empty-img-3" */}
        {/*       d="M149.121 33.292l-6.83 2.65a1 1 0 0 1-1.317-1.23l1.937-6.207c-2.589-2.944-4.109-6.534-4.109-10.408C138.802 8.102 148.92 0 161.402 0 173.881 0 184 8.102 184 18.097c0 9.995-10.118 18.097-22.599 18.097-4.528 0-8.744-1.066-12.28-2.902z" */}
        {/*     /> */}
        {/*     <g className="ant-empty-img-4" transform="translate(149.65 15.383)"> */}
        {/*       <ellipse cx="20.654" cy="3.167" rx="2.849" ry="2.815" /> */}
        {/*       <path d="M5.698 5.63H0L2.898.704zM9.259.704h4.985V5.63H9.259z" /> */}
        {/*     </g> */}
        {/*   </g> */}
        {/* </svg> */}
        <Box
          sx={{
            mt: 1,
          }}
        >
          {gettext("No Rows")}
        </Box>
      </StyledGridOverlay>
    );
  };

  return (
    <>
      {beforeTable}

      {infoText && (
        <Alert severity="info">
          {infoText.split("\n").map((line, index) => (
            <div key={index}>{line}</div>
          ))}
        </Alert>
      )}
      {/*{rows.length > 0 && (*/}
      <DataGrid
        rows={rows}
        // rows={[]}
        columns={columns}
        initialState={props.initialState || propsDefault.initialState}
        pageSizeOptions={props.pageSizeOptions || propsDefault.pageSizeOptions}
        // checkboxSelection={
        //   props.checkboxSelection || propsDefault.checkboxSelection
        // }
        autoHeight
        loading={props.loading || propsDefault.loading}
        sx={{
          marginTop: 10,
          overflow: "visible",
          fontSize: "0.875rem",
          border: "none",
          "--DataGrid-overlayHeight": "300px",
          "&, [class^=MuiDataGrid-main]": {
            // border: "1px solid"
          },
          "&, [class^=MuiDataGrid-root]": {
            // border: "none"
          },
          ".MuiDataGrid-columnSeparator": {
            display: "none",
          },
          ".MuiDataGrid-columnHeader": {
            backgroundColor: "grey.50",
            color: "grey.500",
          },
          ".MuiDataGrid-columnHeaderTitle": {
            fontWeight: "bold",
            // overflow: "visible"
          },
          "& .MuiDataGrid-row.total-row": {
            backgroundColor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(99, 102, 241, 0.22) !important"
                : "#eef2ff !important",
            fontWeight: "700 !important",
            borderTop: "2px solid #6366f1 !important",
            borderBottom: "2px solid #6366f1 !important",
            "&:hover": {
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(99, 102, 241, 0.32) !important"
                  : "#e0e7ff !important",
            },
          },
          "& .MuiDataGrid-main": {
            borderLeft: "1px solid",
            borderRight: "1px solid",
            borderColor: "divider", // theme.palette.divider
            borderRadius: 1, // optional
          },
        }}
        // showToolbar={true}
        showToolbar={showGridToolbar}
        slots={{
          toolbar: CustomToolbar,
          noRowsOverlay: CustomNoRowsOverlay,
          loadingOverlay: LinearProgress as any,
          // showQuickFilter: true
        }}
        {...(props.checkboxSelection ? { rowSelectionModel } : {})}
        keepNonExistentRowsSelected={true}
        onRowSelectionModelChange={handleOnRowSelectionModelChange}
        onPaginationModelChange={props.onPaginationModelChange}
        onSortModelChange={props.onSortModelChange}
        onFilterModelChange={props.onFilterModelChange}
        getRowClassName={
          typeof props.getRowClassName === "function"
            ? props.getRowClassName
            : undefined
        }
        {...propsDataGrid}
      />
      {/*)}*/}

      {afterTable}

      {/* </TableBox> */}
    </>
  );
};

TableView1.propTypes = {
  title: string,
  columns: array.isRequired,
  rows: array,
  excelData: array,
  exportData: array,
  checkboxSelection: bool,
  pageSizeOptions: array,
  initialState: object,
  loading: bool,
  preToolbar: element,
  postToolbar: element,
  beforeTable: element,
  afterTable: element,
  headerRight: element,
  selectedIds: array,
  onRowSelectionModelChange: func,
  onClickDeleteMultiple: func,
  onChangeEnergyStatement: func,
  showGridToolbar: bool,
  showGridToolbarExport: bool,
  showGridToolbarEnergyStatementExport: bool,
  showGridToolbarTariffStatementExport: bool,
  showGridToolbarCustomExport: bool,
  showGridToolbarQuickFilter: bool,
  showGridToolbarDeleteButton: bool,
  showEnergyStatementType: bool,
  fileNameForExport: string,
  selectedEnergyStatement: string,
  rowCount: number,
  infoText: string,
  dataModel: string,
  propsDataGrid: object,
  onPaginationModelChange: func,
  onSortModelChange: func,
  onFilterModelChange: func,
  dataMode: string,
  disableColumnSorting: bool,
  disableColumnSelector: bool,
  columnGroupingModel: array,
  getRowClassName: func,
};
